import { createI18n } from 'vue-i18n';
import { nextTick } from 'vue';
// Direct import of locale files instead of using unplugin
import en from '../locales/en.json';
import es from '../locales/es.json';
import ca from '../locales/ca.json';

// Available locales configuration
export const SUPPORT_LOCALES = ['en', 'es', 'ca'];
export const DEFAULT_LOCALE = 'en';

// Language metadata for SEO
export const LOCALE_INFO = {
  en: {
    name: 'English',
    domain: '', // Default domain (no prefix)
    iso: 'en-US',
    dir: 'ltr'
  },
  es: {
    name: 'Español',
    domain: 'es',
    iso: 'es-ES',
    dir: 'ltr'
  },
  ca: {
    name: 'Català',
    domain: 'ca',
    iso: 'ca-ES',
    dir: 'ltr'
  }
};

// Create messages object
const messages = {
  en,
  es,
  ca
};

// Get saved locale from localStorage
export function getSavedLocale() {
  return localStorage.getItem('user-locale');
}

// Save locale to localStorage
function saveLocale(locale) {
  localStorage.setItem('user-locale', locale);
}

// Extract locale from URL path
export function getLocaleFromPath(path) {
  const segments = path.split('/').filter(Boolean);
  const firstSegment = segments[0];
  
  if (SUPPORT_LOCALES.includes(firstSegment)) {
    return firstSegment;
  }
  
  return DEFAULT_LOCALE;
}

// Remove locale from path
export function removeLocaleFromPath(path) {
  const segments = path.split('/').filter(Boolean);
  const firstSegment = segments[0];
  
  if (SUPPORT_LOCALES.includes(firstSegment) && firstSegment !== DEFAULT_LOCALE) {
    return '/' + segments.slice(1).join('/');
  }
  
  // If path starts with default locale, also remove it for consistency
  if (firstSegment === DEFAULT_LOCALE) {
    const remaining = segments.slice(1).join('/');
    return remaining ? '/' + remaining : '/';
  }
  
  return path;
}

// Add locale to path
export function addLocaleToPath(path, locale) {
  if (locale === DEFAULT_LOCALE) {
    return removeLocaleFromPath(path);
  }
  
  const cleanPath = removeLocaleFromPath(path);
  return `/${locale}${cleanPath === '/' ? '' : cleanPath}`;
}

// Get route for specific locale
export function getRouteForLocale(route, locale) {
  const path = typeof route === 'string' ? route : route.path;
  return addLocaleToPath(path, locale);
}

// Detect initial locale - URL always wins
function getInitialLocale() {
  return getLocaleFromPath(window.location.pathname);
}

// Create i18n instance
export const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: getInitialLocale(),
  fallbackLocale: DEFAULT_LOCALE,
  messages,
  silentTranslationWarn: true,
  silentFallbackWarn: true
});

// Set locale with persistence and DOM updates
export async function setI18nLanguage(locale, force = false, path = null) {
  let finalLocale = locale;
  
  // Only check URL if not forcing the locale (i.e., when called from router guard)
  // When force=true, trust the passed locale (e.g., when switching languages)
  if (!force) {
    // Use provided path or fall back to window.location.pathname
    const pathToCheck = path || window.location.pathname;
    const urlLocale = getLocaleFromPath(pathToCheck);
    const hasExplicitLocale = urlLocale !== DEFAULT_LOCALE && 
      (pathToCheck === `/${urlLocale}` || pathToCheck.startsWith(`/${urlLocale}/`));
    
    finalLocale = hasExplicitLocale ? urlLocale : locale;
  }
  
  i18n.global.locale.value = finalLocale;
  saveLocale(finalLocale);
  document.documentElement.setAttribute('lang', LOCALE_INFO[finalLocale].iso);
  document.documentElement.setAttribute('dir', LOCALE_INFO[finalLocale].dir);
  
  await nextTick();
}

// Initialize i18n with proper document setup
export function setupI18n() {
  const locale = i18n.global.locale.value;
  document.documentElement.setAttribute('lang', LOCALE_INFO[locale].iso);
  document.documentElement.setAttribute('dir', LOCALE_INFO[locale].dir);
  return i18n;
}

export default i18n; 